import React, { useState, useRef } from 'react';
import { grcService } from '../../services/grcService';
import { useIngestionProgress } from '../../context/IngestionProgressContext';

interface FrameworkUploaderProps {
  onUploadComplete: (frameworkId: number) => void;
  onCancel: () => void;
}

type FrameworkType = 'auto' | 'standard' | 'regulation' | 'law' | 'benchmark' | 'guideline' | 'custom';
type ProcessingStatus = 'idle' | 'creating' | 'uploading' | 'processing' | 'complete' | 'error';

interface ProcessingState {
  status: ProcessingStatus;
  progress: number;
  message: string;
  details?: string;
}

export const FrameworkUploader: React.FC<FrameworkUploaderProps> = ({ onUploadComplete, onCancel }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { startTracking } = useIngestionProgress();
  
  const [frameworkName, setFrameworkName] = useState('');
  const [frameworkCode, setFrameworkCode] = useState('');
  const [frameworkType, setFrameworkType] = useState<FrameworkType>('auto');
  const [frameworkVersion, setFrameworkVersion] = useState('1.0');
  const [description, setDescription] = useState('');
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processingState, setProcessingState] = useState<ProcessingState>({
    status: 'idle',
    progress: 0,
    message: ''
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      // Auto-populate name from filename if empty
      if (!frameworkName) {
        const baseName = file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
        setFrameworkName(baseName);
        // Generate code from name
        const code = baseName.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10);
        setFrameworkCode(code);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const allowedExts = ['.pdf', '.txt', '.doc', '.docx', '.html', '.htm', '.md', '.rtf', '.csv', '.xml'];
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (allowedExts.includes(ext)) {
        setSelectedFile(file);
        if (!frameworkName) {
          const baseName = file.name.replace(/\.[^/.]+$/, '').replace(/_/g, ' ');
          setFrameworkName(baseName);
          const code = baseName.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 10);
          setFrameworkCode(code);
        }
      }
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile || !frameworkName || !frameworkCode) {
      alert('Molimo popunite sva obavezna polja i izaberite fajl.');
      return;
    }

    try {
      // Step 1: Create framework
      setProcessingState({
        status: 'creating',
        progress: 10,
        message: 'Kreiranje framework-a...'
      });

      const createResult = await grcService.createFramework({
        name: frameworkName,
        code: frameworkCode,
        framework_type: frameworkType,
        version: frameworkVersion,
        description: description || `${frameworkType}: ${frameworkName}`,
        status: 'pending'
      });

      if (!createResult.success || !createResult.id) {
        throw new Error(createResult.error || 'Greška pri kreiranju framework-a');
      }

      const frameworkId = createResult.id;

      // Step 2: Initialize ingestion to get log_id immediately
      setProcessingState({
        status: 'processing',
        progress: 10,
        message: 'Priprema obrade...',
        details: 'Inicijalizacija AI obrade dokumenta...'
      });

      const initResult = await grcService.initIngestion(frameworkId, selectedFile.name);
      
      if (initResult.success && initResult.log_id) {
        // Start progress tracking IMMEDIATELY before the blocking ingest call
        startTracking(initResult.log_id, frameworkId, selectedFile.name);
      }

      // Step 3: Upload & Process with AI (this is the blocking call)
      setProcessingState({
        status: 'processing',
        progress: 15,
        message: 'AI obrada dokumenta...',
        details: 'Dokument se obrađuje u pozadini. Možete nastaviti rad — progres se prati u donjem desnom uglu.'
      });

      const ingestResult = await grcService.ingestDocument(frameworkId, selectedFile, initResult.log_id);

      if (!ingestResult.success) {
        throw new Error(ingestResult.error || 'Greška pri obradi dokumenta');
      }

      // Step 4: Complete
      setProcessingState({
        status: 'complete',
        progress: 100,
        message: 'Obrada završena!',
        details: `Ekstraktovano ${ingestResult.controls_extracted || 0} kontrola/zahteva iz ${ingestResult.pages_processed || 0} segmenata.`
      });

      // Wait a bit then navigate
      setTimeout(() => {
        onUploadComplete(frameworkId);
      }, 2000);

    } catch (error: any) {
      setProcessingState({
        status: 'error',
        progress: 0,
        message: 'Greška',
        details: error.message || 'Nepoznata greška'
      });
    }
  };

  const isProcessing = ['creating', 'uploading', 'processing'].includes(processingState.status);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <span className="text-2xl">📤</span>
          Upload Framework / Standard / Regulativa
        </h2>

        {/* Processing Overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 rounded-xl flex flex-col items-center justify-center z-10">
            <div className="w-16 h-16 relative mb-4">
              <div className="absolute inset-0 border-4 border-purple-200 dark:border-purple-900 rounded-full"></div>
              <div 
                className="absolute inset-0 border-4 border-purple-600 rounded-full border-t-transparent animate-spin"
                style={{ animationDuration: '1s' }}
              ></div>
              <span className="absolute inset-0 flex items-center justify-center text-2xl">
                {processingState.status === 'creating' && '🏗️'}
                {processingState.status === 'uploading' && '⬆️'}
                {processingState.status === 'processing' && '🤖'}
              </span>
            </div>
            <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {processingState.message}
            </p>
            {processingState.details && (
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center max-w-md">
                {processingState.details}
              </p>
            )}
            <div className="w-64 h-2 bg-gray-200 dark:bg-gray-700 rounded-full mt-4 overflow-hidden">
              <div 
                className="h-full bg-purple-600 transition-all duration-500"
                style={{ width: `${processingState.progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-500 mt-2">{processingState.progress}%</p>
          </div>
        )}

        {/* Complete State */}
        {processingState.status === 'complete' && (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-xl font-bold text-green-600 dark:text-green-400 mb-2">
              {processingState.message}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">{processingState.details}</p>
            <p className="text-sm text-gray-500 mt-4">Preusmeravanje na dashboard...</p>
          </div>
        )}

        {/* Error State */}
        {processingState.status === 'error' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-semibold mb-2">
              <span>❌</span> {processingState.message}
            </div>
            <p className="text-red-600 dark:text-red-400 text-sm">{processingState.details}</p>
            <button
              onClick={() => setProcessingState({ status: 'idle', progress: 0, message: '' })}
              className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded hover:bg-red-200 dark:hover:bg-red-700 text-sm"
            >
              Pokušaj ponovo
            </button>
          </div>
        )}

        {/* Form */}
        {processingState.status === 'idle' && (
          <>
            {/* File Drop Zone */}
            <div
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all mb-6 ${
                selectedFile
                  ? 'border-green-400 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt,.doc,.docx,.html,.htm,.md,.rtf,.csv,.xml"
                onChange={handleFileSelect}
                className="hidden"
              />
              {selectedFile ? (
                <>
                  <span className="text-4xl mb-2 block">📄</span>
                  <p className="font-semibold text-gray-900 dark:text-white">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                    Kliknite da promenite fajl
                  </p>
                </>
              ) : (
                <>
                  <span className="text-4xl mb-2 block">📤</span>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    Prevucite dokument ovde
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    ili kliknite da izaberete fajl
                  </p>
                  <p className="text-xs text-gray-400 mt-3">
                    Podržani formati: PDF, TXT, DOCX, HTML, MD, RTF, CSV, XML
                  </p>
                  <p className="text-xs text-purple-500 mt-1">
                    Tip dokumenta nije bitan — AI prepoznaje strukturu iz sadržaja
                  </p>
                </>
              )}
            </div>

            {/* Framework Details */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Naziv Framework-a *
                </label>
                <input
                  type="text"
                  value={frameworkName}
                  onChange={(e) => setFrameworkName(e.target.value)}
                  placeholder="npr. ISO 27001:2022"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kod *
                </label>
                <input
                  type="text"
                  value={frameworkCode}
                  onChange={(e) => setFrameworkCode(e.target.value.toUpperCase())}
                  placeholder="npr. ISO27001"
                  maxLength={15}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tip
                </label>
                <select
                  value={frameworkType}
                  onChange={(e) => setFrameworkType(e.target.value as FrameworkType)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="auto">🤖 Auto-detect (AI prepoznaje iz sadržaja)</option>
                  <option value="standard">Standard (ISO, NIST, CIS...)</option>
                  <option value="regulation">Regulativa (GDPR, NIS2, DORA...)</option>
                  <option value="law">Zakon (Zakon o zaštiti podataka...)</option>
                  <option value="benchmark">Benchmark (CIS Benchmark...)</option>
                  <option value="guideline">Smernice / Guideline</option>
                  <option value="custom">Custom / Internal Policy</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Verzija
                </label>
                <input
                  type="text"
                  value={frameworkVersion}
                  onChange={(e) => setFrameworkVersion(e.target.value)}
                  placeholder="npr. 2022"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Opis (opciono)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Kratak opis framework-a..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* AI Processing Info */}
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🤖</span>
                <div>
                  <h4 className="font-semibold text-purple-800 dark:text-purple-200">AI Analiza Dokumenta</h4>
                  <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                    AI automatski analizira sadržaj dokumenta i kreira strukturu:
                  </p>
                  <ul className="text-sm text-purple-600 dark:text-purple-400 mt-2 list-disc list-inside">
                    <li>Prepoznaje tip dokumenta iz sadržaja (standard, regulativa, benchmark...)</li>
                    <li>Poglavlja i sekcije — automatski iz strukture dokumenta</li>
                    <li>Članke i tačke — ekstrakcija po hijerarhiji</li>
                    <li>Zahteve i kontrole — svaka tačka postaje kontrola</li>
                    <li>Kategorizacija (tip provere, ozbiljnost, primenjivost)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Odustani
              </button>
              <button
                onClick={handleSubmit}
                disabled={!selectedFile || !frameworkName || !frameworkCode}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <span>🚀</span>
                Pokreni AI Obradu
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FrameworkUploader;
